// Router shape per design_handoff_rev9_portal/README.md §"Router".
// Marketing + auth + portal route trees in one React app. PortalLayout
// wraps the auth-gated subtree and (when real auth lands) enforces login.

import { Navigate, Route, Routes } from 'react-router-dom';

import { AuthProvider, RequireAuth, RequireManager2FA, RequireManagerOrSuperUser, RequireN8, RequireRole, RequireSuperUser } from './lib/auth';
import { PreferencesProvider } from './lib/preferences';

import Home from './routes/marketing/Home';
import Approach from './routes/marketing/Approach';
import MarketingContact from './routes/marketing/Contact';

import SignIn from './routes/auth/SignIn';
import SignUp from './routes/auth/SignUp';
import JoinCompany from './routes/auth/JoinCompany';
import VerifyEmail from './routes/auth/VerifyEmail';
import TwoFactorSetup from './routes/auth/TwoFactorSetup';
import PasswordReset from './routes/auth/PasswordReset';

import PortalLayout from './routes/portal/PortalLayout';
import Clients from './routes/portal/Clients';
import Investors from './routes/portal/Investors';
// ClientOnboard (SMA per-client onboarding) is retired from the manager UI
// (16.d.3.5, fund-only-going-forward). The component + POST /ops/clients +
// the SMA tables are KEPT (reversible); just no route/nav entry points to it.
import Recon from './routes/portal/Recon';
import Dashboard from './routes/portal/Dashboard';
import Disclosures from './routes/portal/Disclosures';
import Fund from './routes/portal/Fund';
import FundOnboard from './routes/portal/FundOnboard';
import FundSettings from './routes/portal/FundSettings';
import FundAccounts from './routes/portal/FundAccounts';
import FundAccruals from './routes/portal/FundAccruals';
import FundHoldings from './routes/portal/FundHoldings';
import FundActivity from './routes/portal/FundActivity';
import FundStatements from './routes/portal/FundStatements';
import FundLayout from './routes/portal/FundLayout';
import FundInvestorDetail from './routes/portal/FundInvestorDetail';
import Holdings from './routes/portal/Holdings';
import WalletBalances from './routes/portal/WalletBalances';
import WalletActivity from './routes/portal/WalletActivity';
import Performance from './routes/portal/Performance';
import Activity from './routes/portal/Activity';
import Statements from './routes/portal/Statements';
import StatementViewer from './routes/portal/StatementViewer';
import Documents from './routes/portal/Documents';
import Audit from './routes/portal/Audit';
import Settings from './routes/portal/Settings';
import PortalContact from './routes/portal/Contact';
import AccountCredentials from './routes/admin/AccountCredentials';
import PriceOverrides from './routes/admin/PriceOverrides';
import Impersonate from './routes/admin/Impersonate';

export default function App() {
  return (
    <AuthProvider>
      <PreferencesProvider>
      <Routes>
        {/* Marketing */}
        <Route path="/" element={<Home />} />
        <Route path="/approach" element={<Approach />} />
        <Route path="/contact" element={<MarketingContact />} />

        {/* Auth */}
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/sign-up" element={<SignUp />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/join/:token" element={<JoinCompany />} />
        <Route path="/reset-password" element={<PasswordReset />} />

        {/* Forced 2FA enrollment (17.E) — logged-in but OUTSIDE the
            RequireManager2FA gate, so a manager without a confirmed TOTP
            device can reach the enrollment page (else: redirect loop). */}
        <Route path="/security/2fa-setup" element={<RequireAuth><TwoFactorSetup /></RequireAuth>} />

        {/* Portal — auth-gated; managers must have 2FA enrolled (17.E) */}
        <Route
          element={
            <RequireAuth>
              <RequireManager2FA>
                <PortalLayout />
              </RequireManager2FA>
            </RequireAuth>
          }
        >
          <Route path="/investors" element={<RequireRole role="manager"><Investors /></RequireRole>} />
          <Route path="/clients" element={<RequireRole role="manager"><Clients /></RequireRole>} />
          <Route path="/recon" element={<RequireRole role="manager"><Recon /></RequireRole>} />
          <Route path="/wallet-balances" element={<RequireRole role="manager"><RequireN8><WalletBalances /></RequireN8></RequireRole>} />
          <Route path="/wallet-balances/activity" element={<RequireRole role="manager"><RequireN8><WalletActivity /></RequireN8></RequireRole>} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/fund" element={<Fund />} />
          <Route path="/fund/new" element={<RequireManagerOrSuperUser><FundOnboard /></RequireManagerOrSuperUser>} />
          {/* Fund sub-routes nested under a shared layout + subnav (16.d.4.5a).
              Parent gates manager; children inherit it. The investor drill-down
              stays a separate route (no subnav). A bare /fund/:id redirects to
              the /fund landing. */}
          <Route path="/fund/:fundId" element={<RequireRole role="manager"><FundLayout /></RequireRole>}>
            <Route index element={<Navigate to="/fund" replace />} />
            <Route path="settings" element={<FundSettings />} />
            <Route path="accounts" element={<FundAccounts />} />
            <Route path="accruals" element={<FundAccruals />} />
            <Route path="holdings" element={<FundHoldings />} />
            <Route path="activity" element={<FundActivity />} />
            <Route path="statements" element={<FundStatements />} />
          </Route>
          <Route path="/fund/:fundId/investors/:investorId" element={<RequireRole role="manager"><FundInvestorDetail /></RequireRole>} />
          <Route path="/disclosures" element={<Disclosures />} />
          <Route path="/holdings" element={<Holdings />} />
          <Route path="/performance" element={<Performance />} />
          <Route path="/activity" element={<Activity />} />
          <Route path="/statements" element={<Statements />} />
          <Route path="/statements/:id" element={<StatementViewer />} />
          <Route path="/documents" element={<Documents />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/portal/contact" element={<PortalContact />} />
          <Route
            path="/admin/accounts/:accountId/credentials"
            element={<RequireManagerOrSuperUser><AccountCredentials /></RequireManagerOrSuperUser>}
          />
          <Route
            path="/admin/price-overrides"
            element={<RequireRole role="manager"><PriceOverrides /></RequireRole>}
          />
          <Route
            path="/admin/impersonate"
            element={<RequireSuperUser><Impersonate /></RequireSuperUser>}
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </PreferencesProvider>
    </AuthProvider>
  );
}
